import React, { useState } from 'react';
import { Modal, Form, Input, Button, Table, Space, Tag, message, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axiosApiInstance from '../../../api/axiosClient';

interface TrackSearchModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectTrack: (track: API.MusicDistribution.ArtTrack) => void;
  releaseId: string;
}

const TrackSearchModal: React.FC<TrackSearchModalProps> = ({
  visible,
  onClose,
  onSelectTrack,
  releaseId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<API.MusicDistribution.ArtTrack[]>([]);

  const handleSearch = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      if (values.track_id) params.append('track_id', values.track_id);
      if (values.title) params.append('title', values.title);
      if (values.isrc) params.append('isrc', values.isrc);
      if (values.upc) params.append('upc', values.upc);
      if (values.release_title) params.append('release_title', values.release_title);

      // Make API call
      const response = await axiosApiInstance.get(`/api/products/search/art_tracks?${params.toString()}`);
      setSearchResults(response.data);

      if (response.data.length === 0) {
        message.info('No tracks found matching your search criteria');
      }
    } catch (error) {
      console.error('Error searching tracks:', error);
      message.error('Failed to search tracks');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTrack = (track: API.MusicDistribution.ArtTrack) => {
    onSelectTrack(track);
    onClose();
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'ISRC',
      dataIndex: 'isrc',
      key: 'isrc',
    },
    {
      title: 'Audio Source',
      key: 'audioSource',
      render: (_, record: API.MusicDistribution.ArtTrack) => {
        const sourceRelease = record.releases?.find(r => r.id === record.audio_file_release_id);
        return sourceRelease ? (
          <Tag color="blue">{sourceRelease.name}</Tag>
        ) : (
          record.filename ? <Tag color="green">Has Audio</Tag> : <Tag color="red">No Audio</Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: API.MusicDistribution.ArtTrack) => (
        <Button 
          type="primary" 
          onClick={() => handleSelectTrack(record)}
          disabled={record.audio_file_release_id === releaseId}
        >
          Select
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="Search Existing Tracks"
      open={visible}
      width={800}
      onCancel={onClose}
      footer={null}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearch}
        >
          <Space style={{ width: '100%', marginBottom: 16 }} direction="vertical">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item label="Track ID" name="track_id">
                <Input placeholder="Enter track ID" />
              </Form.Item>
              <Form.Item label="Title" name="title">
                <Input placeholder="Enter track title" />
              </Form.Item>
              <Form.Item label="ISRC" name="isrc">
                <Input placeholder="Enter ISRC" />
              </Form.Item>
              <Form.Item label="UPC" name="upc">
                <Input placeholder="Enter UPC" />
              </Form.Item>
              <Form.Item label="Release Title" name="release_title">
                <Input placeholder="Enter release title" />
              </Form.Item>
            </div>
            <Button 
              type="primary" 
              icon={<SearchOutlined />} 
              onClick={handleSearch}
              style={{ marginBottom: 16 }}
            >
              Search
            </Button>
          </Space>
        </Form>

        <Table
          dataSource={searchResults}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Spin>
    </Modal>
  );
};

export default TrackSearchModal;